import { useState } from "react";
import type { Money, WorkOrderSubletLine } from "@autorepair/contracts";
import { parseMoneyInput } from "../lib/money";
import {
  ModernButton,
  ModernCheckboxRow,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernModal,
  ModernTextarea,
} from "./ui-modern";
import { useConfirmGate } from "../lib/useConfirm";
import { useShopTypeLabels } from "../lib/theme";

export function EditSubletLineModal({
  line,
  // Current shop subletTaxRateSnapshot — see EditPartLineModal.tsx's own
  // comment on this exact pattern.
  taxRate = "0",
  onSave,
  onClose,
}: {
  line: WorkOrderSubletLine;
  taxRate?: string;
  onSave: (values: { description: string; vendorName: string | null; priceAmount: Money; isTaxable?: boolean }) => Promise<void>;
  onClose: () => void;
}) {
  const [description, setDescription] = useState(line.description);
  const [vendorName, setVendorName] = useState(line.vendorName ?? "");
  const [priceAmount, setPriceAmount] = useState<string>(line.priceAmount);
  const [taxExempt, setTaxExempt] = useState(!line.isTaxable);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const confirmGate = useConfirmGate();
  const shopTypeLabels = useShopTypeLabels();
  const showTaxExempt = Number(taxRate) > 0;

  async function save(): Promise<void> {
    const price = parseMoneyInput(priceAmount);
    if (!price) return setError('Amount should look like "150.00".');
    setError(null);
    setSaving(true);
    try {
      await onSave({
        description,
        vendorName: vendorName.trim() || null,
        priceAmount: price,
        isTaxable: showTaxExempt ? !taxExempt : undefined,
      });
      onClose();
    } catch {
      setError("Could not save this sublet line. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    confirmGate.request(() => void save());
  }

  return (
    <ModernModal title={`Edit ${shopTypeLabels.subletLabel} Work`} onCancel={onClose}>
      {error && <ModernErrorBanner message={error} />}
      <form onSubmit={handleSubmit}>
        <ModernField label="What Was It" htmlFor="editSubletDescription" required>
          <ModernTextarea id="editSubletDescription" required uppercase rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          <span className="mt-1 block text-[12px] text-[#8b97a8]">e.g. towing, alignment, machine-shop work</span>
        </ModernField>
        <ModernField label="Who Did the Work" htmlFor="editSubletVendor">
          <ModernInput id="editSubletVendor" uppercase value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
          <span className="mt-1 block text-[12px] text-[#8b97a8]">The outside shop or vendor, if any</span>
        </ModernField>
        <ModernField label="Amount Charged to Customer" htmlFor="editSubletPrice">
          <ModernInput id="editSubletPrice" decimal value={priceAmount} onChange={(e) => setPriceAmount(e.target.value)} />
          {showTaxExempt && (
            <ModernCheckboxRow className="mt-1.5" checked={taxExempt} onChange={setTaxExempt}>
              Tax exempt
            </ModernCheckboxRow>
          )}
        </ModernField>
        <div className="flex gap-2.5">
          <ModernButton type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </ModernButton>
          <ModernButton type="button" variant="secondary" onClick={onClose}>
            Cancel
          </ModernButton>
        </div>
      </form>
      <ModernConfirmDialog {...confirmGate.dialogProps} message={`Save these changes to the ${shopTypeLabels.subletLabel.toLowerCase()} line?`} />
    </ModernModal>
  );
}
